import React from 'react';
import { bool, arrayOf, number, object, objectOf } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { useIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';

import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import SectionHero from './sections/SectionHero';
import SectionFeaturedProducts from './sections/SectionFeaturedProducts';
import SectionTrustBand from './sections/SectionTrustBand';
import SectionProductRow from './sections/SectionProductRow';
import SectionWhyEthy from './sections/SectionWhyEthy';
import SectionCategories from './sections/SectionCategories';
import SectionEcolabels from './sections/SectionEcolabels';
import SectionBrands from './sections/SectionBrands';
import SectionNewsletter from './sections/SectionNewsletter';

import css from './LandingPage.module.css';

// How many category product rows to show beneath the featured grid. Every
// Listing Category gets a browse card, but only the first few get a full row of
// products so the page doesn't grow without bound as categories are added.
const MAX_CATEGORY_ROWS = 4;

/**
 * The categories that have at least one product, so the homepage never shows
 * an empty category (e.g. "Cleaning" before any cleaning product is listed).
 * A category reappears automatically as soon as a product is published in it.
 *
 * The per-category listing count from the API is authoritative. Where a count
 * isn't known (still loading, or its query failed) we fall back to whether any
 * already-loaded listing is filed under the category, and while everything is
 * still loading we keep the category so the page doesn't jump around.
 *
 * @param {Array<{id: string, name: string}>} categories hosted listing categories
 * @param {Object} options
 * @param {Object<string, number>} [options.counts] listings per category id
 * @param {Array<Object>} [options.listings] listings already loaded for the page
 * @param {string} [options.categoryKey] category param prefix, e.g. 'categoryLevel'
 * @param {boolean} [options.loading] whether counts or listings are still loading
 * @returns {Array<{id: string, name: string}>}
 */
export const getCategoriesWithListings = (categories = [], options = {}) => {
  const { counts = {}, listings = [], categoryKey = 'categoryLevel', loading = false } = options;

  const hasLoadedListing = id =>
    listings.some(listing =>
      Object.entries(listing?.attributes?.publicData || {}).some(
        ([key, value]) => key.startsWith(categoryKey) && value === id
      )
    );

  return categories.filter(({ id }) => {
    const count = counts[id];
    if (typeof count === 'number') {
      return count > 0 || hasLoadedListing(id);
    }
    return loading || hasLoadedListing(id);
  });
};

/**
 * Ethy marketplace homepage.
 *
 * A custom-coded, premium marketplace experience (hero, featured products,
 * shop by ecolabel, trust band, category product rows, why Ethy, categories,
 * brands, newsletter). All product, category and ecolabel data is pulled
 * dynamically from Sharetribe, so the page scales gracefully from a handful of
 * listings today to thousands in the future.
 */
export const LandingPageComponent = props => {
  const config = useConfiguration();
  const intl = useIntl();

  const {
    scrollingDisabled,
    listings = [],
    featuredListings = [],
    queryInProgress,
    queryError,
    heroListings = [],
    heroQueryInProgress,
    categoryListingCounts = {},
    categoryCountsInProgress,
  } = props;

  // Categories are read from the hosted Sharetribe config rather than hardcoded,
  // so the browse cards and the product rows below track whatever Listing
  // Categories the marketplace has (and pick up new ones automatically).
  // Categories without any products yet are hidden everywhere on the page.
  const { key: categoryKey, categories: allCategories = [] } = config.categoryConfiguration || {};
  const categories = getCategoriesWithListings(allCategories, {
    counts: categoryListingCounts,
    listings,
    categoryKey,
    loading: !!(queryInProgress || categoryCountsInProgress),
  });
  const categoryRows = categories.slice(0, MAX_CATEGORY_ROWS);

  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    {
      id: 'LandingPage.schemaTitle',
      defaultMessage: '{marketplaceName}: products with verified sustainability claims',
    },
    { marketplaceName }
  );
  const schemaDescription = intl.formatMessage({
    id: 'LandingPage.schemaDescription',
    defaultMessage:
      'Shop products whose specific sustainability claims have been checked against evidence by ethy.',
  });

  return (
    <Page
      className={css.pageRoot}
      title={schemaTitle}
      description={schemaDescription}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.sections}>
          <SectionHero
            categories={categories}
            categoryKey={categoryKey}
            listings={listings}
            queryInProgress={queryInProgress}
            heroListings={heroListings}
            heroQueryInProgress={heroQueryInProgress}
            listingFields={config.listing?.listingFields}
          />
          <SectionFeaturedProducts
            listings={featuredListings}
            queryInProgress={queryInProgress}
            queryError={queryError}
          />
          <SectionEcolabels listingFields={config.listing?.listingFields} />
          <SectionTrustBand />
          {categoryRows.map(category => (
            <SectionProductRow
              key={category.id}
              category={category}
              categoryKey={categoryKey}
              listings={listings}
              queryInProgress={queryInProgress}
            />
          ))}
          <SectionCategories categories={categories} categoryKey={categoryKey} />
          <SectionWhyEthy />
          <SectionBrands
            listings={listings}
            categories={allCategories}
            categoryKey={categoryKey}
            listingImageConfig={config.layout?.listingImage}
          />
          <SectionNewsletter />
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

LandingPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  listings: arrayOf(object),
  featuredListings: arrayOf(object),
  queryInProgress: bool,
  queryError: propTypes.error,
  heroListings: arrayOf(object),
  heroQueryInProgress: bool,
  categoryListingCounts: objectOf(number),
  categoryCountsInProgress: bool,
};

const mapStateToProps = state => {
  const {
    listingIds = [],
    featuredListingIds = [],
    queryInProgress,
    queryError,
    heroListingIds = [],
    heroQueryInProgress,
    categoryListingCounts,
    categoryCountsInProgress,
  } = state.LandingPage || {};
  const listings = getListingsById(state, listingIds);
  const featuredListings = getListingsById(state, featuredListingIds);
  const heroListings = getListingsById(state, heroListingIds);

  return {
    scrollingDisabled: isScrollingDisabled(state),
    listings,
    featuredListings,
    heroListings,
    heroQueryInProgress,
    queryInProgress,
    queryError,
    categoryListingCounts,
    categoryCountsInProgress,
  };
};

const LandingPage = compose(connect(mapStateToProps))(LandingPageComponent);

export default LandingPage;
